import { DataResponse, MetaEntry } from '../lib/types'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Loading from '../components/loading'
import Table from '../components/table'
import Pagination from '../components/pagination'
import ViewType from '../components/view-type'
import ErrorMsg from '../components/error-msg'
import getId from '../lib/get-id'
import { Search } from '@carbon/react'
import { useDebouncedCallback } from 'use-debounce'
import { useQuery } from '../workflows/util'

type Props = {
    loadData: (
        id: number,
        stepIndex: number,
        page: number,
        viewType: string,
        search: string
    ) => Promise<DataResponse>
    canInsert: boolean
    indicatorFn: (id: number, meta: MetaEntry) => string
    fields?: Array<string>
    idField?: string
}

export default function DataEntrySummary(props: Props) {
    const { loadData, canInsert, indicatorFn, fields, idField } = props
    const { id, stepIndex } = useParams()
    const query = useQuery();
    const view = useMemo(() => query.get('view'), [query])
    const [page, setPage] = useState(Number(sessionStorage.getItem('page') || 0))
    const [data, setData] = useState<Array<any>>([])
    const [meta, setMeta] = useState<Array<MetaEntry>>([])
    const [total, setTotal] = useState(0)
    const [viewType, setViewType] = useState(view || sessionStorage.getItem('viewType') || 'all')
    const [loading, setLoading] = useState(true)
    const [err, setErr] = useState('')
    const [search, setSearch] = useState('')
    const navigate = useNavigate()

    useEffect(() => {
        setLoading(true)
        loadData(Number(id), Number(stepIndex), page, viewType, search)
            .then(({ rows, meta, total }) => {
                if (!!rows.length && !!idField && !rows[0].id) {
                    setData(rows.map((row) => ({ id: row[idField], ...row })))
                } else {
                    setData(rows)
                }
                setMeta(meta)
                setTotal(total)
                setLoading(false)
            })
            .catch((err) => setErr(err.message || err))

        sessionStorage.setItem('page', String(page))
        sessionStorage.setItem('viewType', viewType)
    }, [id, stepIndex, page, loadData, viewType, idField, search])

    const viewRow = useCallback(
        (row: any, modKey: boolean) => {
            let path = `/workflows/${id}/steps/${stepIndex}/view/${getId(row)}`
            if (modKey) window.open(window.location.origin + path, '_blank')
            else navigate(path)
        },
        [id, stepIndex, navigate],
    )

    const handleSearch = useDebouncedCallback((value: string) => {
        setLoading(true)
        setSearch(value)
        setPage(0)
    }, 2000)

    return (
        <div>
            {err && <ErrorMsg error={err} />}
            <div className='flex flex-row justify-between items-center'>
                <ViewType value={viewType} onChange={setViewType} changeName='Modified' />
                <span className='bg-black'>
                    <Search
                        size='sm'
                        placeholder='Search'
                        labelText='Search'
                        style={{ borderRadius: 0, boxShadow: 'initial' }}
                        closeButtonLabelText='Clear search input'
                        id='input-search'
                        onChange={(e) => handleSearch(e.target.value)}
                    />
                </span>
            </div>

            {loading ? (
                <Loading />
            ) : (
                <div>
                    <Table
                        data={data}
                        meta={meta}
                        onClick={viewRow}
                        indicatorFn={indicatorFn}
                        fields={fields}
                    />
                    <Pagination page={page} total={total} onChange={setPage} />
                    {canInsert && (
                        <div className='my-4'>
                            <Link
                                to={`/workflows/${id}/steps/${stepIndex}/view/_`}
                                className={`button yellow ${view ? 'disableLink' : ''}`}
                            >
                                <i className='lni lni-circle-plus' /> Insert New Row
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
